import { clsx, type ClassValue } from "clsx"
import { extendTailwindMerge } from "tailwind-merge"

/**
 * tailwind-merge has to be told about our custom font sizes.
 *
 * The scale is defined in tailwind.config.ts as `text-luxury-sm` and friends.
 * Stock tailwind-merge does not know those are FONT SIZES, so it classified them
 * as text COLOURS - which meant that in `cn("text-shell", "text-luxury-sm")` it
 * saw two colours, kept the last, and silently deleted `text-shell`.
 *
 * That is what made the mobile menu's "Book Now" button render dark green text
 * on a dark green background: contrast ratio 1.0, completely invisible. The bug
 * is silent and applies anywhere a colour and a luxury size meet on a component
 * that merges classes, so it is fixed here rather than by reordering classes at
 * each call site.
 */
const twMerge = extendTailwindMerge({
  extend: {
    classGroups: {
      "font-size": [
        {
          text: [
            "luxury-xs",
            "luxury-sm",
            "luxury-base",
            "luxury-lg",
            "luxury-xl",
            "luxury-2xl",
            "luxury-3xl",
            "luxury-4xl",
            "luxury-5xl",
            "luxury-6xl",
          ],
        },
      ],
    },
  },
})

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs))
}

/**
 * Smoothly scrolls to the top of the page
 * @param behavior - The scroll behavior ('smooth' or 'instant')
 */
export function scrollToTop(behavior: ScrollBehavior = 'instant') {
  window.scrollTo({
    top: 0,
    left: 0,
    behavior
  });
}

/**
 * Checks if the current URL contains a hash that should prevent scroll-to-top
 * @param pathname - The current pathname
 * @returns true if scroll-to-top should be prevented
 */
export function shouldPreventScrollToTop(pathname: string): boolean {
  // Allow hash navigation for same-page anchors
  return window.location.hash.length > 0 && window.location.pathname === pathname;
}

export {
  DEFAULT_CONTACT_EMAIL,
  formatContactEmail,
  contactMailto,
  DEFAULT_CONTACT_PHONE,
  DEFAULT_CONTACT_PHONE_E164,
  DEFAULT_CONTACT_PHONE_TEL_DISPLAY,
  formatContactPhone,
  contactTelHref,
  contactSmsHref,
  toE164Phone,
} from "@shared/contact";

/**
 * Generates human-readable alt text from an image filename
 * @param filename - The image filename (e.g., "beach-house-sunset.jpg")
 * @returns Formatted alt text (e.g., "Beach House Sunset")
 */
export function generateAltFromFilename(filename: string): string {
  // Remove file extension
  const nameWithoutExt = filename.replace(/\.[^/.]+$/, '');
  
  // Replace separators with spaces
  const withSpaces = nameWithoutExt.replace(/[-_\.]/g, ' ');
  
  // Capitalize first letter of each word
  const capitalized = withSpaces
    .split(' ')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ');
  
  // Trim and return
  return capitalized.trim();
}
